import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import BasicECharts from 'components/common/BasicEChart';
import Flex from 'components/common/Flex';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { actionGetDashboardStats } from 'stores/dashboard';
import logger from 'util/logger';

import { getDashboardBarOptions } from './dashboardChartOptions';
import {
  getReportSliceDate,
  getReportSliceSigningCount
} from './dashboardReportData';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer
]);

const getMonthRange = monthOffset => {
  const targetMonth = moment().add(monthOffset, 'month');

  return {
    start: targetMonth.clone().startOf('month'),
    end: targetMonth.clone().endOf('month')
  };
};

const buildDailySigningData = (slices, monthStart, monthEnd) => {
  const signingCountsByDate = {};

  _.map(slices, slice => {
    const dateKey = getReportSliceDate(slice, 'YYYY-MM-DD');
    signingCountsByDate[dateKey] =
      (signingCountsByDate[dateKey] || 0) + getReportSliceSigningCount(slice);
  });

  const signingData = [];
  const currentDate = monthStart.clone();

  while (currentDate.isSameOrBefore(monthEnd, 'day')) {
    const dateKey = currentDate.format('YYYY-MM-DD');

    signingData.push({
      date: currentDate.format('MMM D'),
      signing: signingCountsByDate[dateKey] || 0
    });
    currentDate.add(1, 'day');
  }

  return signingData;
};

const SigningWidget = ({
  monthOffset = -1,
  title = "Last Month's Journal",
  summaryPeriodLabel = 'previous month',
  tooltipText = 'Notary eJournal completed in the previous month'
}) => {
  const [dataValues, setDataValues] = useState([]);
  const [dataKeys, setDataKeys] = useState([]);
  const dispatch = useDispatch();
  const tooltipId = `${title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')}-tooltip`;

  useEffect(() => {
    const monthRange = getMonthRange(monthOffset);

    dispatch(
      actionGetDashboardStats(
        monthRange.start.format('YYYY-MM-DD'),
        monthRange.end.format('YYYY-MM-DD HH:mm:ss')
      )
    )
      .then(res => {
        const slices = _.get(res, 'Slices', []);
        const signingData = buildDailySigningData(
          slices,
          monthRange.start,
          monthRange.end
        );

        setDataKeys(_.map(signingData, 'date'));
        setDataValues(_.map(signingData, 'signing'));
      })
      .catch(err => {
        logger.warn('error loading dashboard monthly journal data', err);
        setDataKeys([]);
        setDataValues([]);
      });
  }, [dispatch, monthOffset]);

  return (
    <Card className="h-100 d-flex flex-column" style={{ minHeight: '32rem' }}>
      <Card.Header className="pb-1">
        <h6 className="mb-0 mt-2">
          {title}
          <OverlayTrigger
            container={document.body}
            placement="top"
            popperConfig={{ strategy: 'fixed' }}
            overlay={<Tooltip id={tooltipId}>{tooltipText}</Tooltip>}
          >
            <span className="d-inline-flex align-items-center ms-1">
              <FontAwesomeIcon
                icon={['far', 'question-circle']}
                transform="shrink-1"
                className="text-400"
              />
            </span>
          </OverlayTrigger>
        </h6>
      </Card.Header>

      <Card.Body as={Flex} direction="column" className="pt-1 flex-grow-1">
        <div className="mb-2 flex-shrink-0">
          <p className="text-600 fs--1 mb-0">
            {_.sum(dataValues)} entries completed in the {summaryPeriodLabel}
          </p>
        </div>
        <div className="flex-grow-1 w-100" style={{ minHeight: '24rem' }}>
          <BasicECharts
            echarts={echarts}
            options={getDashboardBarOptions({
              data: dataValues,
              keys: dataKeys,
              xAxisLabel: 'Date',
              xAxisLabelRotate: 90,
              yAxisLabel: 'Entries'
            })}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      </Card.Body>
    </Card>
  );
};

SigningWidget.propTypes = {
  monthOffset: PropTypes.number,
  title: PropTypes.string,
  summaryPeriodLabel: PropTypes.string,
  tooltipText: PropTypes.string
};

export default SigningWidget;
